import type {
    IndustryNodeId,
    IndustryTaxonomyIndex,
} from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import type { FuzzySearchHit } from "app/pages/sales-intelligence/services/fuzzy-search/types";

type SegmentHit = FuzzySearchHit<IndustryNodeId>;

const hitsById = (hits: readonly SegmentHit[]): ReadonlyMap<IndustryNodeId, SegmentHit> =>
    new Map(hits.map((hit) => [hit.id, hit]));

const nearestAncestorHit = (
    taxonomy: IndustryTaxonomyIndex,
    descendantId: IndustryNodeId,
    candidates: ReadonlyMap<IndustryNodeId, SegmentHit>,
): SegmentHit | undefined => {
    for (let cursor = taxonomy.parentById.get(descendantId); cursor; cursor = taxonomy.parentById.get(cursor)) {
        const hit = candidates.get(cursor);

        if (hit) {
            return hit;
        }
    }

    return undefined;
};

const chainForFinalHit = (
    taxonomy: IndustryTaxonomyIndex,
    segmentHitMaps: readonly ReadonlyMap<IndustryNodeId, SegmentHit>[],
    finalHit: SegmentHit,
): readonly SegmentHit[] | null => {
    const chain: SegmentHit[] = [finalHit];
    const finalSegmentIndex = segmentHitMaps.length - 1;

    for (let segmentIndex = finalSegmentIndex - 1; segmentIndex >= 0; segmentIndex -= 1) {
        const ancestorHit = nearestAncestorHit(taxonomy, chain[0].id, segmentHitMaps[segmentIndex]);

        if (!ancestorHit) {
            return null;
        }

        chain.unshift(ancestorHit);
    }

    return chain;
};

const aggregateScore = (chain: readonly SegmentHit[]): number => chain.reduce((sum, hit) => sum + hit.score, 0);

const compareChains = (left: readonly SegmentHit[], right: readonly SegmentHit[]): number => {
    const scoreDelta = aggregateScore(left) - aggregateScore(right);

    if (scoreDelta !== 0) {
        return scoreDelta;
    }

    const leftId = left[left.length - 1].id;
    const rightId = right[right.length - 1].id;

    return leftId.localeCompare(rightId);
};

export const hierarchicalIndustryPathSearch = (
    taxonomy: IndustryTaxonomyIndex,
    segmentHits: readonly (readonly SegmentHit[])[],
    maxResults: number,
): readonly SegmentHit[] => {
    const segmentHitMaps = segmentHits.map(hitsById);
    const finalSegmentHits = segmentHits[segmentHits.length - 1];

    return finalSegmentHits
        .flatMap((finalHit) => {
            const chain = chainForFinalHit(taxonomy, segmentHitMaps, finalHit);

            return chain ? [chain] : [];
        })
        .sort(compareChains)
        .slice(0, maxResults)
        .map((chain) => chain[chain.length - 1]);
};
